// T019: Effect Resolver

import type {
  EffectType,
  EffectTrigger,
  EffectContext,
  EffectResult,
  StateChange,
  GameState,
  Card,
  TurnPhase,
  CombatInfo,
} from '../../types';
import { EffectRegistry } from './EffectRegistry';

export interface ResolveOptions {
  trigger: EffectTrigger;
  sourceCard: Card;
  targetCards?: Card[];
  combatInfo?: CombatInfo;
}

export interface ResolveResult {
  success: boolean;
  allChanges: StateChange[];
  effectResults: Map<EffectType, EffectResult>;
}

/**
 * Resolves effects during game actions.
 * Handles effect ordering, activation checks, and state change collection.
 */
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class EffectResolver {
  /**
   * Resolve all effects for a card based on trigger.
   */
  static resolve(
    gameState: GameState,
    phase: TurnPhase,
    options: ResolveOptions
  ): ResolveResult {
    const { trigger, sourceCard, targetCards, combatInfo } = options;

    // Get all effect types from the source card
    const cardEffects = sourceCard.activeEffects ?? [];
    if (cardEffects.length === 0) {
      return {
        success: true,
        allChanges: [],
        effectResults: new Map(),
      };
    }

    // Build context
    const context: EffectContext = {
      gameState,
      sourceCard,
      targetCards,
      phase,
      combatInfo,
    };

    // Get effect instances from registry
    const effects = EffectRegistry.getMultiple(cardEffects as EffectType[]);

    // Filter by trigger
    const triggeredEffects = effects.filter((e) => e.trigger === trigger);

    // Execute each effect
    const allChanges: StateChange[] = [];
    const effectResults = new Map<EffectType, EffectResult>();

    for (const effect of triggeredEffects) {
      if (effect.canActivate(context)) {
        const result = effect.execute(context);
        effectResults.set(effect.type, result);

        if (result.success) {
          allChanges.push(...result.stateChanges);
        }
      }
    }

    return {
      success: true,
      allChanges,
      effectResults,
    };
  }

  /**
   * Resolve effects for multiple cards.
   */
  static resolveMultiple(
    gameState: GameState,
    phase: TurnPhase,
    cards: Card[],
    trigger: EffectTrigger,
    combatInfo?: CombatInfo
  ): ResolveResult {
    const allChanges: StateChange[] = [];
    const effectResults = new Map<EffectType, EffectResult>();

    for (const card of cards) {
      const result = this.resolve(gameState, phase, {
        trigger,
        sourceCard: card,
        combatInfo,
      });

      allChanges.push(...result.allChanges);
      for (const [type, effectResult] of result.effectResults) {
        effectResults.set(type, effectResult);
      }
    }

    return {
      success: true,
      allChanges,
      effectResults,
    };
  }

  /**
   * Check if a card has a specific effect that can activate.
   */
  static canActivateEffect(
    gameState: GameState,
    phase: TurnPhase,
    card: Card,
    effectType: EffectType,
    combatInfo?: CombatInfo
  ): boolean {
    const effect = EffectRegistry.get(effectType);
    if (!effect) return false;

    if (!card.activeEffects?.includes(effectType)) return false;

    const context: EffectContext = {
      gameState,
      sourceCard: card,
      phase,
      combatInfo,
    };

    return effect.canActivate(context);
  }

  /**
   * Get all activatable effects for a card at current phase.
   */
  static getActivatableEffects(
    gameState: GameState,
    phase: TurnPhase,
    card: Card,
    trigger: EffectTrigger,
    combatInfo?: CombatInfo
  ): EffectType[] {
    const cardEffects = card.activeEffects ?? [];
    const activatable: EffectType[] = [];

    for (const effectType of cardEffects) {
      if (this.canActivateEffect(gameState, phase, card, effectType as EffectType, combatInfo)) {
        const effect = EffectRegistry.get(effectType as EffectType);
        if (effect && effect.trigger === trigger) {
          activatable.push(effectType as EffectType);
        }
      }
    }

    return activatable;
  }
}
